/**
 * StoryContainer
 * 
 * Displays story snippets on the MAP VIEW (full screen map)
 * Shows exploration narrative when player enters new hexes
 * Now with AI narrator voice support!
 * 
 * ⚠️ IMPORTANT: This component has a PARALLEL version in ActionUI.tsx
 * Both display the same types of content (story, loading, actionCompletionMessage, etc.)
 * When modifying what content is displayed here, CHECK if the same change
 * should be made to the story container section in ActionUI.tsx (around line 990-1050)
 * 
 * Example: If you add support for a new message type here, add it to ActionUI too.
 * 
 * NOTE: Quest content intentionally only appears in ActionUI, not here on the map view,
 * because quests require interactive choices which aren't suitable for the map view's
 * simple story container. Players must use the Action screen for quest interactions.
 */

import React, { useEffect, useRef, useState } from 'react';
import { FolkloreContainer } from './FolkloreContainer';
import { TerrainType, AxialCoord } from '../types/terrain';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import './StoryContainer.css';

export interface StoryContainerProps {
  storyText: string | null;
  audioUrl?: string | null;
  isLoading: boolean;
  onFightClick?: () => void;
  hasMonster?: boolean;
  isUnexploredHex?: boolean; // Flag to show "Unexplored Land" indicator
  travelState?: {
    destinationHex: AxialCoord;
    arrivalTime: number;
    route: AxialCoord[];
    currentRouteIndex: number;
    nextHexArrivalTime: number;
  } | null;
  destinationTerrain?: TerrainType;
  onTravelComplete?: () => void;
  onTravelCancel?: () => void;
  onHexArrival?: (hex: AxialCoord) => void; // Called when arriving at each hex during travel
  actionCompletionMessage?: string | null;
}

// Helper function to format time remaining
function formatTime(ms: number): string {
  const totalSeconds = Math.ceil(ms / 1000);
  
  if (totalSeconds >= 3600) {
    // HH:MM:SS for times >= 1 hour
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  } else {
    // MM:SS for times < 1 hour
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}

// Helper function to get terrain display name
function getTerrainName(terrain: TerrainType): string {
  switch (terrain) {
    case TerrainType.Mountain:
      return 'Mountains';
    case TerrainType.Forest:
      return 'Forest';
    case TerrainType.Hills:
      return 'Hills';
    case TerrainType.Tundra:
      return 'Tundra';
    case TerrainType.River:
      return 'River';
    case TerrainType.Settlement:
      return 'Settlement';
    case TerrainType.Desert:
      return 'Desert';
    case TerrainType.Ocean:
      return 'Ocean';
    default:
      return 'Unknown Terrain';
  }
}

export const StoryContainer: React.FC<StoryContainerProps> = ({
  storyText,
  audioUrl,
  isLoading,
  onFightClick,
  hasMonster = false,
  isUnexploredHex = false,
  travelState = null,
  destinationTerrain,
  onTravelComplete,
  onTravelCancel,
  onHexArrival,
  actionCompletionMessage = null,
}) => {
  // Use shared audio player (persists across view changes)
  const audioPlayer = useAudioPlayer();
  const { isPlaying, audioError, toggle: toggleAudio } = audioPlayer;
  
  const [timeRemaining, setTimeRemaining] = useState(0);
  const hasCompletedRef = useRef(false); // Track if callback already fired (persists across renders)
  const onTravelCompleteRef = useRef(onTravelComplete); // Track latest callback without causing re-runs
  const renderCountRef = useRef(0);
  
  // Track renders
  renderCountRef.current++;
  console.log(`🎨 [STORY_CONTAINER] Render #${renderCountRef.current}`);
  console.log('   storyText:', storyText ? `${storyText.substring(0, 50)}...` : 'null');
  console.log('   audioUrl:', audioUrl);

  // Audio is now managed by shared AudioPlayerContext - no local audio handling needed

  // Keep callback ref in sync (doesn't trigger effect re-run)
  useEffect(() => {
    onTravelCompleteRef.current = onTravelComplete;
  }, [onTravelComplete]);
  
  // DEBUG: Track when actionCompletionMessage is received
  useEffect(() => {
    if (actionCompletionMessage) {
      console.log('📨 StoryContainer received actionCompletionMessage:', actionCompletionMessage);
    }
  }, [actionCompletionMessage]);

  // Handle travel countdown and hex-by-hex progression
  useEffect(() => {
    if (!travelState) {
      setTimeRemaining(0);
      hasCompletedRef.current = false; // Reset when travel state clears
      return;
    }

    // Use a ref to track current travelState to avoid closure issues
    const travelStateRef = { current: travelState };
    
    // Update countdown every 100ms
    const interval = setInterval(() => {
      // CRITICAL: Check if travelState is null (travel was stopped, e.g., by monster encounter)
      // This prevents any further encounter checks after travel stops
      if (!travelStateRef.current) {
        console.log('🛑 TRAVEL DEBUG - Interval detected travelState is null, stopping all checks');
        clearInterval(interval);
        setTimeRemaining(0);
        return;
      }
      
      // Check if we've arrived at the next hex in the route
      const nextHexTimeRemaining = travelStateRef.current.nextHexArrivalTime - Date.now();
      
      if (nextHexTimeRemaining <= 0) {
        // Double-check travelState is still valid before proceeding
        if (!travelStateRef.current) {
          console.log('🛑 TRAVEL DEBUG - Travel stopped during hex arrival check, aborting');
          clearInterval(interval);
          return;
        }
        
        // Arrived at next hex - check for monster encounter
        const currentHexIndex = travelStateRef.current.currentRouteIndex + 1;
        if (currentHexIndex < travelStateRef.current.route.length) {
          const arrivedHex = travelStateRef.current.route[currentHexIndex];
          // Call onHexArrival to check for monster and handle progression
          if (onHexArrival) {
            onHexArrival(arrivedHex);
          }
        }
      }
      
      // Check if we've reached the final destination
      // Only complete travel when we've actually progressed through all hexes
      const currentIndex = travelStateRef.current.currentRouteIndex;
      const totalHexes = travelStateRef.current.route.length;
      const hasReachedFinalDestination = currentIndex >= totalHexes - 1;
      
      // Check final destination arrival time (for display and completion)
      const finalRemaining = travelStateRef.current.arrivalTime - Date.now();
      
      // Only complete travel if:
      // 1. We've reached the final hex in the route (currentIndex >= totalHexes - 1)
      // 2. AND the final arrival time has passed
      if (hasReachedFinalDestination && finalRemaining <= 0) {
        clearInterval(interval);
        setTimeRemaining(0);
        
        // Only fire callback once using REF (persists across re-renders!)
        if (onTravelCompleteRef.current && !hasCompletedRef.current) {
          hasCompletedRef.current = true;
          onTravelCompleteRef.current();
        }
      } else {
        // Show time remaining until next hex arrival (for multi-hex travel)
        setTimeRemaining(Math.max(0, nextHexTimeRemaining));
      }
    }, 100);
    
    // Update ref when travelState changes
    travelStateRef.current = travelState;

    return () => {
      clearInterval(interval);
    };
  }, [travelState?.arrivalTime, travelState?.nextHexArrivalTime, travelState?.currentRouteIndex, travelState?.route, travelState, onHexArrival, destinationTerrain]); // Re-run when travel state changes

  // Show travel countdown
  if (travelState && timeRemaining > 0) {
    const terrainName = destinationTerrain ? getTerrainName(destinationTerrain) : 'destination';
    const formattedTime = formatTime(timeRemaining);
    
    // Calculate progress through route
    const currentHex = travelState.currentRouteIndex + 1; // +1 because index is 0-based
    const totalHexes = travelState.route.length;
    const progressText = `(hex ${currentHex} of ${totalHexes})`;

    return (
      <div className="story-container-wrapper">
        <FolkloreContainer variant="story" className="story-container">
          <div className="story-content" style={{ textAlign: 'center', padding: '0.35rem' }}>
            <p className="story-loading" style={{ fontSize: '15px', margin: '0 0 2px 0' }}>
              Travelling to {terrainName}...
            </p>
            <p style={{ fontSize: '11px', color: '#8B7355', margin: '0 0 4px 0', opacity: 0.9 }}>
              {progressText}
            </p>
            <p style={{ 
              fontSize: '28px', 
              fontWeight: 'bold', 
              color: '#8B4513',
              fontFamily: "'Cinzel', 'Lora', 'Georgia', serif",
              margin: '4px 0',
              letterSpacing: '0.05em',
            }}>
              {formattedTime}
            </p>
            <p style={{ fontSize: '12px', color: '#6B5A4A', opacity: 0.8, margin: '0 0 6px 0' }}>
              Your story will begin when you arrive
            </p>
            {onTravelCancel && (
              <button
                onClick={onTravelCancel}
                style={{
                  padding: '6px 14px',
                  backgroundColor: '#8B4513',
                  color: '#F5E6D3',
                  border: '2px solid #6B3410',
                  borderRadius: '6px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#A0522D';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#8B4513';
                }}
              >
                Cancel Journey
              </button>
            )}
          </div>
        </FolkloreContainer>
      </div>
    );
  }

  // Always render container to prevent unmount/remount flicker
  // Use opacity to show/hide content smoothly
  const showLoading = isLoading || (travelState && timeRemaining > 0);
  const showContent = !showLoading && (storyText || actionCompletionMessage);
  
  // Don't render at all if we have no story and aren't loading
  if (!showLoading && !showContent) {
    return null;
  }
  
  return (
    <div className="story-container-wrapper">
      <FolkloreContainer variant="story" className="story-container">
        {showLoading && (
          <p className="story-loading">
            {isLoading && 'Exploring...'}
          </p>
        )}
        {showContent && (
          <div className="story-content">
            {actionCompletionMessage ? (
              <p className="action-completion-message">{actionCompletionMessage}</p>
            ) : (
              <>
                <p className="story-text">{storyText}</p>
                
                {/* Show Unexplored Land indicator UNDER the story text */}
                {isUnexploredHex && (
                  <div style={{
                    backgroundColor: 'transparent',
                    border: '1px solid #C4B5A0',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    marginTop: '12px',
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#5C4A3A',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}>
                    You are in unexplored land
                  </div>
                )}
                
                {hasMonster && onFightClick && (
                  <button 
                    className="story-fight-button"
                    onClick={onFightClick}
                  >
                    You are forced to fight...
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </FolkloreContainer>
    </div>
  );
};

